import { useState } from "react";
import Select from "react-select";
import countryList from "react-select-country-list";
import { useNavigate } from "react-router-dom";
import Header from "../PublicHeader";
import Footer from "../Footer";
import axios from "../../api/axiosInstance";
import { siteKey } from "../../config";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';


export default function RegisterStep1({ onNext }: { onNext: (id: number) => void }) {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone_number: "",
        country: "",
        address: "",
        website: "",
        business_hours: "",
        cuisine_type: "",
        description: "",
        whatsapp_number: ""
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [previewLogo, setPreviewLogo] = useState<string | null>(null);
    const countryOptions = countryList().getData();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);



    const validate = () => {
        const newErrors: { [key: string]: string } = {};
        if (!formData.name.trim()) newErrors.name = "Restaurant name is required.";
        if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Valid email is required.";
        if (!formData.phone_number.trim() || !/^\+?[\d\s-]{7,}$/.test(formData.phone_number)) newErrors.phone_number = "Enter a valid phone number.";
        if (!formData.country.trim()) newErrors.country = "Country is required.";
        if (!formData.address.trim() || formData.address.length < 10) newErrors.address = "Address is too short.";
        if (formData.website && !/^https?:\/\/.+\..+/.test(formData.website)) newErrors.website = "Invalid website URL.";
        if (formData.whatsapp_number && !/^\+?[\d\s-]{7,}$/.test(formData.whatsapp_number)) newErrors.whatsapp_number = "Invalid WhatsApp number.";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleCountryChange = (selectedOption: any) => {
        setFormData({ ...formData, country: selectedOption.label });
        setErrors(prev => ({ ...prev, country: "" }));
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setLogoFile(file);
            setPreviewLogo(URL.createObjectURL(file));
        }
    };

    // const handleSubmit = async (e: React.FormEvent) => {
    //     e.preventDefault();
    //     if (!validate()) return;
    //
    //     const formDataToSend = new FormData();
    //     Object.entries(formData).forEach(([key, value]) => {
    //         formDataToSend.append(key, value);
    //     });
    //     if (logoFile) formDataToSend.append("logo", logoFile);
    //
    //     try {
    //         const response = await axios.post<{ pending_id: number; error?: string }>(
    //             "/api/register-step1",
    //             formDataToSend,
    //             {
    //                 headers: { "Content-Type": "multipart/form-data" }
    //             }
    //         );
    //         if (response.status === 201) {
    //             onNext(response.data.pending_id);
    //         } else {
    //             alert(response.data.error || "Something went wrong.");
    //         }
    //     } catch (error: any) {
    //         console.error("Registration Error:", error);
    //         alert(error.response?.data?.error || "Failed to register. Please try again.");
    //     }
    // };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!executeRecaptcha) {
            console.log("Recaptcha not yet available");
            return;
        }

        const token = await executeRecaptcha("register");

        if (!token) {
            setStatusMessage("⚠️ reCAPTCHA verification failed");
            return;
        }


        if (!validate()) return;

        setIsSubmitting(true);
        setStatusMessage("Processing your details…");

        const formDataToSend = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
            formDataToSend.append(key, value);
        });
        // 👉 include token in your formData or request
        formDataToSend.append("recaptcha_token", token);

        if (logoFile) formDataToSend.append("logo", logoFile);

        try {
            const response = await axios.post<{ pending_id: number; error?: string }>(
                "/api/register-step1",
                formDataToSend,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            if (response.status === 201) {
                setStatusMessage("✅ Moving to Step 2...");
                setTimeout(() => {
                    onNext(response.data.pending_id);
                }, 1000);
            } else {
                setStatusMessage("❌ " + (response.data.error || "Something went wrong."));
            }
        } catch (error: any) {
            setStatusMessage("❌ " + (error.response?.data?.error || "Failed to register."));
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <div className="min-h-screen flex flex-col bg-gray-50 w-full">
            <Header />
            <main className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-6xl bg-white shadow-md rounded-lg p-6 md:p-10 mx-auto">
                    <div className="text-center mb-6">
                        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-800">🍽️ Restaurant Registration</h2>
                        <p className="text-gray-600 mt-2 text-lg">Step 1: General Information</p>
                    </div>
                    <div className="flex flex-col items-center mb-6">
                        <label className="relative cursor-pointer">
                            <img src={previewLogo || "/default-logo.jpg"} alt="Restaurant Logo" className="w-32 h-32 md:w-40 md:h-40 object-cover rounded-full border shadow-lg" />
                            <input type="file" className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" onChange={handleLogoChange} />
                        </label>
                        <p className="text-sm text-gray-600 mt-2 text-center">Click image to upload logo</p>
                    </div>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-gray-700 font-semibold">Restaurant Name</label>
                            <input type="text" name="name" className="w-full p-3 border rounded-lg" placeholder="Enter restaurant name" onChange={handleChange} required />
                            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold">Email</label>
                            <input type="email" name="email" className="w-full p-3 border rounded-lg" placeholder="Enter email" onChange={handleChange} required />
                            {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold">Phone Number</label>
                            <input type="text" name="phone_number" className="w-full p-3 border rounded-lg" placeholder="Enter phone number" onChange={handleChange} required />
                            {errors.phone_number && <p className="text-red-500 text-sm">{errors.phone_number}</p>}
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold">Country</label>
                            <Select options={countryOptions} onChange={handleCountryChange} className="w-full" placeholder="Select a country..." />
                            {errors.country && <p className="text-red-500 text-sm">{errors.country}</p>}
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold">Full Address</label>
                            <input type="text" name="address" className="w-full p-3 border rounded-lg" placeholder="Enter full address" onChange={handleChange} required />
                            {errors.address && <p className="text-red-500 text-sm">{errors.address}</p>}
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold">Website (Optional)</label>
                            <input type="text" name="website" className="w-full p-3 border rounded-lg" placeholder="Enter website URL" onChange={handleChange} />
                            {errors.website && <p className="text-red-500 text-sm">{errors.website}</p>}
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold">Business Hours</label>
                            <input type="text" name="business_hours" className="w-full p-3 border rounded-lg" placeholder="e.g., 9 AM - 9 PM" onChange={handleChange} />
                        </div>
                        <div>
                            <label className="block text-gray-700 font-semibold">Cuisine Type</label>
                            <input type="text" name="cuisine_type" className="w-full p-3 border rounded-lg" placeholder="e.g., Italian, Chinese" onChange={handleChange} />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-gray-700 font-semibold">Short Description</label>
                            <textarea name="description" className="w-full p-3 border rounded-lg" placeholder="Describe your restaurant..." onChange={handleChange}></textarea>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-gray-700 font-semibold">WhatsApp Number</label>
                            <input type="text" name="whatsapp_number" className="w-full p-3 border rounded-lg" placeholder="+1 234 567 8901" onChange={handleChange} />
                            {errors.whatsapp_number && <p className="text-red-500 text-sm">{errors.whatsapp_number}</p>}
                        </div>
                        <div className="md:col-span-2 flex justify-center">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full sm:w-1/2 ${
                                    isSubmitting ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                                } text-white font-bold py-3 px-4 rounded-lg transition mt-4 flex items-center justify-center`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg
                                            className="animate-spin mr-2 h-5 w-5 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v8H4z"
                                            ></path>
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    "Next ➡️"
                                )}
                            </button>

                        </div>
                    </form>
                    {statusMessage && (
                        <div className="text-sm text-center mt-4 text-blue-600 animate-pulse">
                            {statusMessage}
                        </div>
                    )}
                </div>
                {}
            </main>
            <Footer/>
        </div>
    );
}
